import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import { downloadFile, downloadPost } from '../lib/download';
import {
  band,
  type QuestionStat,
  type ResultRow,
  type ResultSortKey,
  type ScoreRow,
} from '../lib/results';
import { PageHeader } from '../components/PageHeader';
import { Person } from '../components/Avatar';
import { Band } from '../components/Band';
import { ModuleTag } from '../components/ModuleTag';
import { Banner } from '../components/Banner';
import { useConfirm } from '../components/Confirm';
import { useAuth } from '../store/auth';
import { useWorkspace } from '../store/workspace';
import { SortHeader } from '../components/SortHeader';
import { RowMenu } from '../components/RowMenu';
import { nextSort, type SortState } from '../lib/sorting';
import { arrangeScores, type ScoreSortKey } from '../lib/score-table';

interface Listing {
  results: ResultRow[];
  total: number;
  /** Released across the whole selection, not merely the page that came back. */
  released: number;
}

export function ResultsPage() {
  const user = useAuth((s) => s.user);
  const workspaceOrgId = useWorkspace((w) => w.organizationId);
  // A teacher's own centre, or the one the administrator has open: the
  // administrator belongs to no centre, so their own id names nothing here.
  const orgId = user?.organizationId ?? workspaceOrgId ?? null;
  /** Whether marks reach students at once is one setting for the whole centre: the administrator's. */
  const canSetInstant = user?.role === 'SUPER_ADMIN';
  const [rows, setRows] = useState<ResultRow[]>([]);
  const [total, setTotal] = useState(0);
  const [released, setReleased] = useState(0);
  const [instant, setInstant] = useState(true);
  const [savingInstant, setSavingInstant] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    api
      .get<{ organization: { instantResults?: boolean } }>(`/organizations/${orgId}`)
      .then(({ data }) => setInstant(data.organization.instantResults ?? true))
      .catch(() => undefined);
  }, [orgId]);

  /**
   * Changing it never rewrites what a candidate has already been shown: the
   * setting is read when a paper is marked, so turning it off holds the next
   * one rather than taking a band back off somebody who has read it.
   */
  async function saveInstant(next: boolean) {
    setSavingInstant(true);
    try {
      await api.patch(`/organizations/${orgId}`, { instantResults: next });
      setInstant(next);
    } catch (err) {
      setError(errorMessage(err, 'Could not change that setting'));
    } finally {
      setSavingInstant(false);
    }
  }
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Which paper's sittings are on screen.
   *
   * Read from the URL on the way in, so the library can send a teacher
   * straight here: "1 sat it" beside a paper that cannot be deleted is a fact
   * about work to do, and this is where the work is done.
   */
  const [testId, setTestId] = useState<string>(
    () => new URLSearchParams(window.location.search).get('testId') ?? '',
  );
  const [papers, setPapers] = useState<{ id: string; title: string }[]>([]);
  const [stats, setStats] = useState<QuestionStat[] | null>(null);
  /** Sittings as they came in, or one row per candidate with their bands. */
  const [view, setView] = useState<'sittings' | 'students'>('sittings');
  const { ask, dialog } = useConfirm();
  /**
   * The ticked sittings, by id. Cleared whenever the list is fetched again —
   * a new paper, a new search, a new order — because a tick on a row that is
   * no longer on screen is a deletion the teacher cannot see they asked for.
   */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectingAll, setSelectingAll] = useState(false);
  const [table, setTable] = useState<ScoreRow[] | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortState<ResultSortKey>>({
    key: 'graded',
    direction: 'desc',
  });

  /**
   * Choosing a paper asks the server for that paper's sittings rather than
   * sieving the page already in hand. Filtering here looked right until a
   * centre passed a page of results: the table then showed the sittings that
   * happened to have been fetched, while Release acted on all of them.
   */
  async function load(paper = testId, search = query, order = sort) {
    setLoading(true);
    try {
      const { data } = await api.get<Listing>('/results', {
        params: {
          ...(paper ? { testId: paper } : {}),
          ...(search.trim() ? { q: search.trim() } : {}),
          sort: order.key,
          direction: order.direction,
        },
      });
      setRows(data.results);
      setTotal(data.total);
      setReleased(data.released ?? 0);
      setSelected(new Set());
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load results'));
    } finally {
      setLoading(false);
    }
  }

  /**
   * A new term or a new column goes back to the server, because the search and
   * the ordering both happen there — the list is paged, and sieving the page
   * in hand would search a fraction of the term.
   *
   * Typing waits a moment first: a teacher looking for "Karimova" would
   * otherwise send eight searches and race their answers back.
   */
  useEffect(() => {
    if (view !== 'sittings') return undefined;
    const timer = setTimeout(() => void load(testId, query, sort), query ? 250 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sort, view]);

  function sortBy(key: ResultSortKey) {
    // A mark, a date or a release is read biggest-and-latest first; a name and
    // a paper are read A to Z.
    setSort((current) => nextSort(current, key, key === 'student' || key === 'paper' ? 'asc' : 'desc'));
  }

  /**
   * The list is paged, so it must never look complete when it is not. A table
   * silently missing a student's result is worse than one that says so.
   */
  async function loadMore() {
    setLoadingMore(true);
    try {
      const { data } = await api.get<Listing>('/results', {
        params: {
          offset: rows.length,
          ...(testId ? { testId } : {}),
          ...(query.trim() ? { q: query.trim() } : {}),
          sort: sort.key,
          direction: sort.direction,
        },
      });
      setRows((current) => [...current, ...data.results]);
      setTotal(data.total);
      setReleased(data.released ?? 0);
    } catch (err) {
      setError(errorMessage(err, 'Could not load more results'));
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    void load();
    // The papers a centre has, so the filter lists every one of them rather
    // than only those whose results reached the first page.
    api
      .get<{ tests: { id: string; title: string }[] }>('/tests')
      .then(({ data }) => setPapers(data.tests.map((t) => ({ id: t.id, title: t.title }))))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shown = rows;

  /**
   * Takes one sitting off the record.
   *
   * A trial run — a paper opened to look at the screen and submitted blank —
   * is a real row: it sits in the class average, on the student's own page,
   * and it blocks the paper from ever being deleted. Confirmed by name and
   * paper, because it cannot be undone and the student's answers go with it.
   */
  async function clearSitting(row: ResultRow) {
    const ok = await ask({
      title: `Clear ${row.student.fullName}’s sitting of “${row.test.title}”?`,
      body: (
        <>
          <p>The mark and everything they typed are deleted, and the paper opens for them again.</p>
          <p>This cannot be undone.</p>
        </>
      ),
      confirmLabel: 'Clear sitting',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/results/${row.id}`);
      setNotice(`Cleared ${row.student.fullName}'s sitting of "${row.test.title}".`);
      await load(testId);
    } catch (err) {
      setError(errorMessage(err, 'Could not clear that sitting'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * The ticked Writing sittings. Their scripts are cleared two days after each
   * result reaches its candidate, so a teacher who wants to keep a class's
   * writing takes it away as one document.
   */
  const writingPicked = rows.filter((r) => selected.has(r.id) && r.test.module === 'WRITING');

  async function downloadWriting() {
    setBusy(true);
    setError(null);
    try {
      await downloadPost(
        '/results/scripts.docx',
        { ids: writingPicked.map((r) => r.id) },
        `writing-scripts-${new Date().toISOString().slice(0, 10)}.docx`,
      );
      setNotice(
        `Writing from ${writingPicked.length} ${writingPicked.length === 1 ? 'candidate' : 'candidates'} downloaded.`,
      );
    } catch (err) {
      setError(errorMessage(err, 'Could not download that writing'));
    } finally {
      setBusy(false);
    }
  }

  const allShownSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someShownSelected = rows.some((r) => selected.has(r.id));

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllShown() {
    setSelected(allShownSelected ? new Set() : new Set(rows.map((r) => r.id)));
  }

  /**
   * Every sitting that matches, not only the pages loaded so far.
   *
   * The list comes 200 at a time, so the box in the header can only tick what
   * is on screen. When there is more, this fetches the rest first — a teacher
   * who ticks "all" and deletes must not be left with the 150 they never saw.
   */
  async function selectEveryMatch() {
    setSelectingAll(true);
    setError(null);
    try {
      let all = rows;
      while (all.length < total) {
        const { data } = await api.get<Listing>('/results', {
          params: {
            offset: all.length,
            ...(testId ? { testId } : {}),
            ...(query.trim() ? { q: query.trim() } : {}),
            sort: sort.key,
            direction: sort.direction,
          },
        });
        if (data.results.length === 0) break;
        all = [...all, ...data.results];
      }
      setRows(all);
      setSelected(new Set(all.map((r) => r.id)));
    } catch (err) {
      setError(errorMessage(err, 'Could not load every result'));
    } finally {
      setSelectingAll(false);
    }
  }

  /**
   * Clears every ticked sitting, on the terms that clear one: the mark and the
   * answers go, and each paper opens for its candidate to sit again. Sent in
   * batches the server accepts, and counted back from what it says it did.
   */
  async function clearSelected() {
    const ids = [...selected];
    if (ids.length === 0) return;
    const papers = new Set(rows.filter((r) => selected.has(r.id)).map((r) => r.test.title));
    const ok = await ask({
      title: `Delete ${ids.length} ${ids.length === 1 ? 'result' : 'results'}?`,
      body: (
        <>
          <p>
            The marks and everything the {ids.length === 1 ? 'candidate' : 'candidates'} typed are
            deleted{papers.size === 1 ? ` for “${[...papers][0]}”` : ` across ${papers.size} papers`},
            and each paper opens for its candidate to sit again.
          </p>
          <p>This cannot be undone.</p>
        </>
      ),
      confirmLabel: `Delete ${ids.length}`,
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    let deleted = 0;
    let skipped = 0;
    try {
      for (let i = 0; i < ids.length; i += 500) {
        const { data } = await api.post<{ deleted: number; skipped: number }>('/results/clear', {
          ids: ids.slice(i, i + 500),
        });
        deleted += data.deleted;
        skipped += data.skipped;
      }
      setNotice(
        `Deleted ${deleted} ${deleted === 1 ? 'result' : 'results'}.` +
          (skipped > 0
            ? ` ${skipped} could not be deleted — already gone, or not yours to delete.`
            : ''),
      );
    } catch (err) {
      setError(
        errorMessage(err, 'Could not delete those results') +
          (deleted > 0 ? ` (${deleted} were deleted before it stopped.)` : ''),
      );
    } finally {
      await load(testId);
      setBusy(false);
    }
  }

  async function act(path: string, label: string) {
    if (!testId) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post<Record<string, number>>(path, { testId });
      setNotice(`${label}: ${Object.values(data)[0]} result(s).`);
      await load(testId);
    } catch (err) {
      setError(errorMessage(err, `Could not ${label.toLowerCase()}`));
    } finally {
      setBusy(false);
    }
  }

  /** Authenticated, so it goes through the shared blob download. */
  async function download(path: string, filename: string) {
    setBusy(true);
    setError(null);
    try {
      await downloadFile(path, filename);
    } catch (err) {
      setError(errorMessage(err, 'Could not export those results'));
    } finally {
      setBusy(false);
    }
  }

  async function loadStats() {
    if (!testId) return;
    try {
      const { data } = await api.get<{ questions: QuestionStat[] }>(`/results/stats/${testId}`);
      setStats(data.questions);
    } catch (err) {
      setError(errorMessage(err, 'Could not load question statistics'));
    }
  }

  async function loadTable() {
    setLoading(true);
    try {
      const { data } = await api.get<{ rows: ScoreRow[] }>('/results/table');
      setTable(data.rows);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the score table'));
    } finally {
      setLoading(false);
    }
  }

  const paper = papers.find((p) => p.id === testId);

  return (
    <>
      {dialog}
      <PageHeader
        title="Results"
        meta={
          <p className="muted">
            {rows.some((r) => r.awaitingMarking) && (
              <>
                <span className="mono">{rows.filter((r) => r.awaitingMarking).length}</span> waiting
                to be marked ·{' '}
              </>
            )}
            {rows.length < total ? (
              <>
                showing <span className="mono">{rows.length}</span> of{' '}
                <span className="mono">{total}</span> marked sittings
              </>
            ) : (
              <>
                <span className="mono">{total}</span> sitting{total === 1 ? '' : 's'}
              </>
            )}
          </p>
        }
      />

      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}
        {notice && <Banner tone="success">{notice}</Banner>}

        {/* Two ways of reading the same marks: sitting by sitting, which is how
            they arrive, and candidate by candidate, which is how a class is
            taught. */}
        <div className="row" role="tablist" aria-label="How to read the results">
          <button
            className={`btn ${view === 'sittings' ? 'btn--primary' : ''}`}
            role="tab"
            aria-selected={view === 'sittings'}
            onClick={() => setView('sittings')}
          >
            By sitting
          </button>
          <button
            className={`btn ${view === 'students' ? 'btn--primary' : ''}`}
            role="tab"
            aria-selected={view === 'students'}
            onClick={() => {
              setView('students');
              if (!table) void loadTable();
            }}
          >
            Score table
          </button>
        </div>

        {view === 'sittings' && papers.length > 0 && (
          <section className="panel stack">
            <div className="row">
              <label className="field" style={{ minWidth: '18rem' }}>
                <span className="field__label">Paper</span>
                <select
                  className="field__input"
                  value={testId}
                  onChange={(e) => {
                    const next = e.target.value;
                    setTestId(next);
                    setStats(null);
                    void load(next);
                  }}
                >
                  <option value="">All papers</option>
                  {papers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/*
              * Whether a mark reaches the candidate on its own, said next to
              * the button it replaces. A teacher looking for "why can my
              * students see this already" looks here, and a teacher who wants
              * them to see it sooner finds it without being told where.
              */}
            <div className="release">
              {canSetInstant ? (
                <label className="release__switch">
                  <input
                    type="checkbox"
                    checked={instant}
                    disabled={savingInstant || !orgId}
                    onChange={(e) => void saveInstant(e.target.checked)}
                  />
                  <span>
                    <strong>Show marks to students as soon as a paper is marked</strong>
                    <span className="release__note">
                      {instant
                        ? 'On, for every teacher in this centre. Listening and Reading appear the moment a student submits; Writing appears when it has been marked. Nobody needs to release anything.'
                        : 'Off, for every teacher in this centre. Nothing reaches a student until a teacher presses “Release to students” below — which is what you want for a mock exam a room sits together.'}
                    </span>
                  </span>
                </label>
              ) : (
                /*
                 * The setting, said rather than offered. It is one switch for
                 * every teacher in the centre, so it is the administrator's —
                 * but a teacher still needs to know whether their students
                 * already see their marks or are waiting for a release.
                 */
                <p className="release__switch">
                  <span>
                    <strong>
                      {instant
                        ? 'Marks reach students as soon as a paper is marked'
                        : 'Marks wait until you release them'}
                    </strong>
                    <span className="release__note">
                      {instant
                        ? 'Listening and Reading appear the moment a student submits; Writing appears when you have marked it. You do not need to release anything.'
                        : 'Nothing reaches a student until you press “Release to students” below.'}{' '}
                      Your centre’s administrator sets this.
                    </span>
                  </span>
                </p>
              )}
            </div>

            {paper && (
              <>
                <p className="muted">
                  <span className="mono">{released}</span> of <span className="mono">{total}</span>{' '}
                  released to students.
                </p>
                <div className="row">
                  <button
                    className="btn btn--primary"
                    disabled={busy || total === 0 || released === total}
                    onClick={() => void act('/results/release', 'Released')}
                  >
                    Release to students
                  </button>
                  <button
                    className="btn"
                    disabled={busy || released === 0}
                    onClick={() => void act('/results/withhold', 'Withheld')}
                  >
                    Withhold
                  </button>
                  <button className="btn" disabled={busy} onClick={() => void act('/results/remark', 'Re-marked')}>
                    Re-mark
                  </button>
                  <button className="btn" onClick={() => void loadStats()}>
                    Question breakdown
                  </button>
                  <button
                    className="btn"
                    disabled={busy}
                    onClick={() => void download(`/results/export/${testId}`, `${paper?.title ?? 'results'}.csv`)}
                  >
                    Export CSV
                  </button>
                </div>
                <p className="field__hint" style={{ maxWidth: '62ch' }}>
                  Re-marking scores every sitting again against the answer keys as they now stand —
                  use it after correcting a key. Bands come from the published conversion for{' '}
                  {shown[0]?.bandTable ?? 'this module'} and are indicative: the real exam adjusts
                  slightly between versions.
                </p>
              </>
            )}
          </section>
        )}

        {view === 'sittings' && stats && (
          <section className="panel">
            <h2 className="panel__title">How the class did, question by question</h2>
            <p className="muted" style={{ marginBottom: 'var(--s-4)' }}>
              A question nobody answered correctly is usually a wrong answer key rather than a hard
              question.
            </p>
            <div className="ledger__wrap">
              <table className="ledger">
                <thead>
                  <tr>
                    <th className="num">Q</th>
                    <th className="num">Correct</th>
                    <th className="num">Blank</th>
                    <th className="num">Over limit</th>
                    <th>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.map((s) => (
                    <tr key={s.questionNumber}>
                      <td className="num">{s.questionNumber}</td>
                      <td className="num">
                        {s.correct}/{s.sat}
                      </td>
                      <td className="num">{s.blank}</td>
                      <td className="num">{s.overLimit}</td>
                      <td>
                        <span
                          className={`tag ${s.percentCorrect === 0 ? 'tag--off' : s.percentCorrect >= 60 ? 'tag--on' : ''}`}
                        >
                          {s.percentCorrect}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {view === 'students' ? (
          <ScoreTable
            rows={table}
            loading={loading}
            busy={busy}
            onExport={() => void download('/results/table/export', 'score-table.csv')}
          />
        ) : loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <>
            {(total > 0 || query.trim()) && (
              <div className="listbar">
                <input
                  type="search"
                  className="field__input listbar__search"
                  placeholder="Find by name, candidate, number or paper"
                  aria-label="Find a sitting by student, candidate number, sign-in number or paper"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query.trim() && !loading && (
                  <p className="muted">
                    <span className="mono">{total}</span> matching{' '}
                    {total === 1 ? 'sitting' : 'sittings'}
                  </p>
                )}
              </div>
            )}

            {shown.length === 0 ? (
          <div className="empty">
            {query.trim() ? (
              <>
                <p>No sitting matches “{query.trim()}”.</p>
                <p className="muted">
                  A student's name or code, their candidate or sign-in number, or the paper's title.
                </p>
                <button className="btn" onClick={() => setQuery('')}>
                  Clear the search
                </button>
              </>
            ) : (
              <>
                <p>No results yet.</p>
                <p className="muted">Results appear here as soon as a student submits a paper.</p>
              </>
            )}
          </div>
        ) : (
          <>
          {/*
            * What is ticked, and what can be done with it. Only while something
            * is ticked, so the page reads as it always has until a teacher
            * starts choosing.
            */}
          {selected.size > 0 && (
            <div className="selectbar" role="region" aria-label="Selected results">
              <p className="selectbar__count">
                <strong className="mono">{selected.size}</strong>{' '}
                {selected.size === 1 ? 'result' : 'results'} selected
                {allShownSelected && rows.length < total && (
                  <>
                    {' '}— only the {rows.length} loaded so far.{' '}
                    <button
                      className="linkbtn"
                      disabled={selectingAll}
                      onClick={() => void selectEveryMatch()}
                    >
                      {selectingAll ? 'Loading…' : `Select all ${total}`}
                    </button>
                  </>
                )}
              </p>
              <div className="row">
                {writingPicked.length > 0 && (
                  <button className="btn" disabled={busy} onClick={() => void downloadWriting()}>
                    Download writing ({writingPicked.length})
                  </button>
                )}
                <button className="btn btn--ghost" onClick={() => setSelected(new Set())}>
                  Clear selection
                </button>
                <button
                  className="btn btn--danger"
                  disabled={busy || selectingAll}
                  onClick={() => void clearSelected()}
                >
                  Delete {selected.size}
                </button>
              </div>
            </div>
          )}
          <div className="ledger__wrap">
            <table className="ledger">
              <thead>
                <tr>
                  <th className="ledger__pick">
                    <input
                      type="checkbox"
                      aria-label={allShownSelected ? 'Unselect all results' : 'Select all results shown'}
                      checked={allShownSelected}
                      ref={(el) => {
                        // Part-ticked: some rows chosen, not all.
                        if (el) el.indeterminate = someShownSelected && !allShownSelected;
                      }}
                      onChange={toggleAllShown}
                    />
                  </th>
                  <SortHeader label="Candidate" column="candidate" sort={sort} onSort={sortBy} numeric />
                  <SortHeader label="Name" column="student" sort={sort} onSort={sortBy} />
                  <SortHeader label="Paper" column="paper" sort={sort} onSort={sortBy} />
                  <SortHeader label="Raw" column="score" sort={sort} onSort={sortBy} numeric />
                  <SortHeader label="Band" column="band" sort={sort} onSort={sortBy} numeric />
                  <SortHeader label="Released" column="released" sort={sort} onSort={sortBy} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => (
                  <tr key={row.id} className={selected.has(row.id) ? 'ledger__row--picked' : undefined}>
                    <td className="ledger__pick">
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.student.fullName}'s ${row.test.title}`}
                        checked={selected.has(row.id)}
                        onChange={() => toggle(row.id)}
                      />
                    </td>
                    <td className="num">{row.student.candidateNumber ?? '—'}</td>
                    <td>
                      <Person name={row.student.fullName} />
                    </td>
                    <td className="muted">
                      <span className="withmark">
                        <ModuleTag module={row.test.module} iconOnly />
                        {row.test.title}
                      </span>
                    </td>
                    <td className="num">
                      {/* Writing has no raw score — an examiner judges it against
                          criteria, so a number out of two would mean nothing. */}
                      {row.test.module === 'WRITING' ? (
                        <span className="muted">—</span>
                      ) : (
                        `${row.rawScore}/${row.total}`
                      )}
                    </td>
                    <td className="num">
                      <Band value={row.bandScore} />
                    </td>
                    <td>
                      {row.awaitingMarking ? (
                        // A Writing paper is not a result until someone reads
                        // it, so this is a task rather than a status.
                        <span className="tag tag--off">Needs marking</span>
                      ) : (
                        <span className={`tag ${row.releasedAt ? 'tag--on' : ''}`}>
                          {row.releasedAt ? 'Released' : 'Withheld'}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row" style={{ justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                        <Link
                          className={`btn btn--sm ${row.awaitingMarking ? 'btn--primary' : ''}`}
                          to={`/results/${row.id}`}
                        >
                          {row.awaitingMarking ? 'Mark' : 'Review'}
                        </Link>
                        <RowMenu
                          label={`More for ${row.student.fullName}’s sitting`}
                          items={[
                            {
                              label: 'Clear sitting',
                              hint: 'Deletes the mark; the paper opens again',
                              icon: 'trash',
                              danger: true,
                              disabled: busy,
                              onSelect: () => void clearSitting(row),
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
            )}
          </>
        )}

        {view === 'sittings' && rows.length < total && (
          <div className="row">
            <button className="btn" disabled={loadingMore} onClick={() => void loadMore()}>
              {loadingMore ? 'Loading…' : `Load the next ${Math.min(200, total - rows.length)}`}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/** A band with the raw score under it, and a link to the sitting it came from. */
function ScoreCellView({ cell }: { cell: ScoreRow['listening'] }) {
  if (!cell) return <span className="muted">—</span>;
  if (cell.awaitingMarking) {
    return (
      <Link className="tag tag--off" to={`/results/${cell.resultId}`}>
        Needs marking
      </Link>
    );
  }
  return (
    <Link to={`/results/${cell.resultId}`} title={cell.testTitle} className="scorecell">
      <span className="scorecell__band mono">{band(cell.band)}</span>
      {cell.total > 0 && (
        <span className="scorecell__raw muted mono">
          {cell.rawScore}/{cell.total}
        </span>
      )}
    </Link>
  );
}

/**
 * The class's marks, one row per candidate and the best band first.
 *
 * Listening and Reading arrive on their own; Writing joins the row when an
 * instructor has marked it, and the table stands without it — which is the
 * state it is in for most of the week.
 */
function ScoreTable({
  rows,
  loading,
  busy,
  onExport,
}: {
  rows: ScoreRow[] | null;
  loading: boolean;
  busy: boolean;
  onExport: () => void;
}) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortState<ScoreSortKey>>({
    key: 'average',
    direction: 'desc',
  });

  // Searched and ordered here rather than at the server: one row per candidate
  // and no paging, so the whole table is already in hand.
  const shown = useMemo(
    () => arrangeScores(rows ?? [], { query, key: sort.key, direction: sort.direction }),
    [rows, query, sort],
  );

  function sortBy(key: ScoreSortKey) {
    setSort((current) => nextSort(current, key, key === 'student' ? 'asc' : 'desc'));
  }

  if (loading && !rows) return <p className="muted">Loading…</p>;
  if (!rows || rows.length === 0) {
    return (
      <div className="empty">
        <p>No results yet.</p>
        <p className="muted">A candidate appears here as soon as they have sat a paper.</p>
      </div>
    );
  }

  return (
    <>
      <div className="listbar">
        <input
          type="search"
          className="field__input listbar__search"
          placeholder="Find a candidate"
          aria-label="Find a candidate by name or candidate number"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="row">
          {query.trim() && (
            <p className="muted">
              <span className="mono">{shown.length}</span> of{' '}
              <span className="mono">{rows.length}</span>
            </p>
          )}
          <button className="btn" disabled={busy} onClick={onExport}>
            Export CSV
          </button>
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="empty">
          <p>Nobody matches “{query.trim()}”.</p>
          <button className="btn" onClick={() => setQuery('')}>
            Show all {rows.length}
          </button>
        </div>
      ) : (
      <div className="ledger__wrap">
        <table className="ledger">
          <thead>
            <tr>
              <SortHeader label="Candidate" column="candidate" sort={sort} onSort={sortBy} numeric />
              <SortHeader label="Name" column="student" sort={sort} onSort={sortBy} />
              <SortHeader label="Listening" column="listening" sort={sort} onSort={sortBy} numeric />
              <SortHeader label="Reading" column="reading" sort={sort} onSort={sortBy} numeric />
              <SortHeader label="Writing" column="writing" sort={sort} onSort={sortBy} numeric />
              <SortHeader label="Average" column="average" sort={sort} onSort={sortBy} numeric />
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr key={row.student.id}>
                <td className="num">{row.student.candidateNumber ?? '—'}</td>
                <td style={{ fontWeight: 600 }}>{row.student.fullName}</td>
                <td className="num">
                  <ScoreCellView cell={row.listening} />
                </td>
                <td className="num">
                  <ScoreCellView cell={row.reading} />
                </td>
                <td className="num">
                  <ScoreCellView cell={row.writing} />
                </td>
                <td className="num" style={{ fontWeight: 700 }}>
                  {band(row.average)}
                  {row.awaitingWriting && (
                    <span className="muted" title="The Writing paper is not marked yet">
                      {' '}
                      *
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      <p className="field__hint" style={{ maxWidth: '62ch' }}>
        The latest sitting of each module, best average first. The average covers the papers that
        have a band — a Writing paper still waiting to be marked is left out rather than counted as
        a zero, and its row is marked with an asterisk. Speaking is not part of this platform, so
        this is an average of the papers sat rather than an IELTS overall band.
      </p>
    </>
  );
}
